import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, redirect, useLoaderData } from 'react-router'
import { NotFoundErrorBoundary } from '#app/components/not-found.tsx'
import { getInstance } from '#app/middleware/i18next.ts'
import {
	DEFAULT_LANGUAGE,
	getHreflangLinks,
	isLegacyLanguage,
	isSupportedLanguage,
} from '#app/utils/i18n.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { type Route } from './+types/_layout.ts'

export async function loader({ params, request, context }: Route.LoaderArgs) {
	const { lang } = params

	if (!lang) {
		// Bare path like /about — we only serve de, so prefix it.
		const url = new URL(request.url)
		return redirect(`/${DEFAULT_LANGUAGE}${url.pathname}${url.search}`)
	}

	if (isLegacyLanguage(lang)) {
		// Site is de-only — rewrite /en/* or /fr/* to /de/*.
		const url = new URL(request.url)
		const rest = url.pathname.slice(`/${lang}`.length)
		return redirect(`/${DEFAULT_LANGUAGE}${rest}${url.search}`)
	}

	if (!isSupportedLanguage(lang)) {
		throw new Response('Not found', { status: 404 })
	}

	// Sync the server-side i18n instance to match the URL language
	const i18n = getInstance(context)
	if (i18n.language !== lang) {
		await i18n.changeLanguage(lang)
	}

	const url = new URL(request.url)
	const siteUrl = process.env.APP_URL || getDomainUrl(request)
	const hreflangLinks = getHreflangLinks(url.pathname, siteUrl)

	return { lang, siteUrl, hreflangLinks }
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data) return []
	return data.hreflangLinks
}

export default function MarketingLayout() {
	const { lang } = useLoaderData<typeof loader>()
	const { i18n } = useTranslation()

	useEffect(() => {
		if (i18n.language !== lang) {
			void i18n.changeLanguage(lang)
		}
	}, [lang, i18n])

	return <Outlet />
}

// An unknown single-segment path like /does-not-exist lands here as an
// unsupported $lang, so this layout renders the 404 hero as well as `$.tsx`.
export const ErrorBoundary = NotFoundErrorBoundary
